import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "../../@/components/ui/button";
import GenericDropdown from "../../components/GenericDropDown";
import { useToast } from "../../@/components/ui/use-toast";
import {
  ORDER_CANCEL_REASONS,
  SERVICE_CANCEL_REASONES,
} from "../../lib/constants";
import { IsServiceTypeVendor } from "../../lib/utils";
import SortupDialog from "../../components/common/SortupDialog";

interface OrderCancelDialogProps {
  statusUpdating: any;
  isNotEditable: any;
  setCancelResone: any;
  currentStep: any;
  perviousStep: any;
  setCurrentStep: (step: number) => void;
}

interface DropdownOption {
  value: string;
  label: string;
}

const OrderCancelDialog: React.FC<OrderCancelDialogProps> = ({
  statusUpdating,
  isNotEditable,
  setCancelResone,
  currentStep,
  perviousStep,
  setCurrentStep,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");
  const { toast } = useToast();

  const reasons: DropdownOption[] = IsServiceTypeVendor()
    ? SERVICE_CANCEL_REASONES
    : ORDER_CANCEL_REASONS;

  const handleReasonChange = (value: any) => {
    if (value == "-1") {
      setSelectedReason("");
      return;
    }
    setSelectedReason(value);
    if (value !== "Other Reason") {
      setOtherReason("");
    }
  };

  const handleCancel = () => {
    const reason =
      selectedReason === "Other Reason" ? otherReason : selectedReason;
    setCancelResone(reason);
    setCurrentStep(6);
    setIsDialogOpen(false);
  };

  const handleOnClick = () => {
    if (perviousStep === 16 || perviousStep === 5 || perviousStep === 28) {
      toast({
        title: "Error",
        description: `${
          perviousStep === 16
            ? "Order is dispatched"
            : perviousStep === 5
            ? "Order is delivered"
            : "Booking is completed"
        } You can't perform this action after ${
          perviousStep === 16
            ? "order is dispatched"
            : perviousStep === 5
            ? "order is delivered"
            : "booking is completed"
        }.`,
      });
      return;
    }
    if (perviousStep === 6 || perviousStep === 15) {
      toast({
        title: "Error",
        description: "You can't perform this action after order is updated.",
      });
      return;
    }
    setIsDialogOpen(true);
  };

  const dialogContent = (
    <>
      {/* Reason dropdown */}
      <div className="my-4">
        <GenericDropdown
          labelValue={selectedReason}
          options={reasons}
          onChange={handleReasonChange}
          placeholder="Select a reason"
          label="Reason"
          error={false}
          isDisabled={false}
          className="w-full justify-center items-center h-10"
          dropdownMenuContentWidth="w-[35vw]"
        />
      </div>

      {/* Input for "Other Reason" */}
      {selectedReason === "Other Reason" && (
        <div className="my-4">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Please specify the reason:
          </label>
          <textarea
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            className="w-full p-2 border rounded-lg resize-none"
            placeholder="Enter your reason here"
            rows={4}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="flex">
        <div className={`h-0.5 lg:w-[7vw] w-[5vw]`} />
        <div
          onClick={handleOnClick}
          className={`flex items-center mt-2 flex-col cursor-pointer`}
        >
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full border 
                ${currentStep === 6 ? "bg-red-500" : "bg-gray-300"}`}
            style={{ position: "relative" }}
          >
            <X color="white" />
          </div>
          {/* Label */}
          <span
            className={`mx-2 text-sm whitespace-nowrap
                ${
                  currentStep === 6
                    ? "text-red-500 font-semibold"
                    : "text-gray-500"
                }`}
          >
            Cancel
          </span>
        </div>
      </div>

      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header="Cancel Order"
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: "Okay",
            onClick: handleCancel,
            disabled:
              !selectedReason ||
              (selectedReason === "Other Reason" && !otherReason),
            className: "text-white",
          },
        ]}
      >
        {dialogContent}
      </SortupDialog>
    </>
  );
};

export default OrderCancelDialog;
